import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { ADMIN_CAPABILITIES } from '@/admin/capabilities'
import { adminService } from '@services/adminService'

export const ADMIN_SESSION_STATES = Object.freeze({
  LOADING: 'loading',
  FORBIDDEN: 'forbidden',
  MFA_ENROLLMENT_REQUIRED: 'mfaEnrollmentRequired',
  READY: 'ready',
  SESSION_EXPIRED: 'sessionExpired',
  UNAVAILABLE: 'unavailable'
})

const AdminSessionContext = createContext(null)

function getResponseCode(error) {
  return (
    error?.response?.data?.code ||
    error?.response?.data?.error?.code ||
    error?.code ||
    null
  )
}

export function classifyAdminSessionError(error) {
  const code = getResponseCode(error)
  const status = error?.response?.status

  if (code === 'ADMIN_MFA_ENROLLMENT_REQUIRED') {
    return ADMIN_SESSION_STATES.MFA_ENROLLMENT_REQUIRED
  }

  if (status === 401) {
    return ADMIN_SESSION_STATES.SESSION_EXPIRED
  }

  if (
    status === 403 ||
    [
      'ADMIN_ACCOUNT_INACTIVE',
      'ADMIN_CAPABILITY_REQUIRED',
      'ADMIN_CONSOLE_DISABLED'
    ].includes(code)
  ) {
    return ADMIN_SESSION_STATES.FORBIDDEN
  }

  return ADMIN_SESSION_STATES.UNAVAILABLE
}

function unwrapData(response) {
  return response?.data ?? response
}

function resolveReadyState(session) {
  if (session?.mfa?.required && !session.mfa.enrolled) {
    return ADMIN_SESSION_STATES.MFA_ENROLLMENT_REQUIRED
  }

  if (!session?.capabilities?.includes(ADMIN_CAPABILITIES.CONSOLE_READ)) {
    return ADMIN_SESSION_STATES.FORBIDDEN
  }

  return ADMIN_SESSION_STATES.READY
}

export function AdminSessionProvider({ children }) {
  const mountedRef = useRef(true)
  const expirationTimerRef = useRef(null)
  const [status, setStatus] = useState(ADMIN_SESSION_STATES.LOADING)
  const [session, setSession] = useState(null)
  const [error, setError] = useState(null)
  const [elevationPrompt, setElevationPrompt] = useState(0)

  const scheduleElevationExpiration = useCallback(expiresAt => {
    clearTimeout(expirationTimerRef.current)
    if (!expiresAt) return

    const delay = new Date(expiresAt).getTime() - Date.now()
    if (delay <= 0) {
      setSession(current =>
        current
          ? {
              ...current,
              elevation: {
                ...current.elevation,
                active: false,
                expiresAt: null
              }
            }
          : current
      )
      return
    }

    expirationTimerRef.current = setTimeout(() => {
      setSession(current =>
        current
          ? {
              ...current,
              elevation: {
                ...current.elevation,
                active: false,
                expiresAt: null
              }
            }
          : current
      )
    }, delay)
  }, [])

  const refresh = useCallback(async () => {
    setStatus(ADMIN_SESSION_STATES.LOADING)
    setError(null)

    try {
      const response = await adminService.getSession()
      const nextSession = unwrapData(response)
      if (!mountedRef.current) return null

      setSession(nextSession)
      setStatus(resolveReadyState(nextSession))
      scheduleElevationExpiration(nextSession?.elevation?.expiresAt)
      return nextSession
    } catch (requestError) {
      if (!mountedRef.current) return null

      setSession(null)
      setError(requestError)
      setStatus(classifyAdminSessionError(requestError))
      return null
    }
  }, [scheduleElevationExpiration])

  useEffect(() => {
    mountedRef.current = true
    refresh()

    return () => {
      mountedRef.current = false
      clearTimeout(expirationTimerRef.current)
    }
  }, [refresh])

  useEffect(() => {
    const handleElevationRequired = () => {
      setSession(current =>
        current
          ? {
              ...current,
              elevation: {
                ...current.elevation,
                active: false,
                expiresAt: null
              }
            }
          : current
      )
      setElevationPrompt(current => current + 1)
    }

    window.addEventListener('admin:elevation-required', handleElevationRequired)
    return () =>
      window.removeEventListener(
        'admin:elevation-required',
        handleElevationRequired
      )
  }, [])

  const elevate = useCallback(
    async totpCode => {
      const response = await adminService.elevateSession(totpCode)
      const elevation = unwrapData(response)

      setSession(current => ({
        ...current,
        elevation: {
          ...current?.elevation,
          ...elevation
        }
      }))
      scheduleElevationExpiration(elevation?.expiresAt)
      return elevation
    },
    [scheduleElevationExpiration]
  )

  const revokeElevation = useCallback(async () => {
    const response = await adminService.revokeElevation()
    const elevation = unwrapData(response)
    clearTimeout(expirationTimerRef.current)
    setSession(current => ({
      ...current,
      elevation: {
        ...current?.elevation,
        ...elevation
      }
    }))
    return elevation
  }, [])

  const capabilities = useMemo(
    () => session?.capabilities || [],
    [session?.capabilities]
  )
  const capabilitySet = useMemo(() => new Set(capabilities), [capabilities])
  const hasCapability = useCallback(
    capability => capabilitySet.has(capability),
    [capabilitySet]
  )

  const value = useMemo(
    () => ({
      status,
      session,
      error,
      loading: status === ADMIN_SESSION_STATES.LOADING,
      ready: status === ADMIN_SESSION_STATES.READY,
      forbidden: status === ADMIN_SESSION_STATES.FORBIDDEN,
      mfaEnrollmentRequired:
        status === ADMIN_SESSION_STATES.MFA_ENROLLMENT_REQUIRED,
      sessionExpired: status === ADMIN_SESSION_STATES.SESSION_EXPIRED,
      unavailable: status === ADMIN_SESSION_STATES.UNAVAILABLE,
      capabilities,
      hasCapability,
      elevationPrompt,
      refresh,
      elevate,
      revokeElevation
    }),
    [
      capabilities,
      elevate,
      elevationPrompt,
      error,
      hasCapability,
      refresh,
      revokeElevation,
      session,
      status
    ]
  )

  return (
    <AdminSessionContext.Provider value={value}>
      {children}
    </AdminSessionContext.Provider>
  )
}

export default AdminSessionContext
